import Avatar from 'ext-ui/components/Avatar/Avatar'
import React, { ReactElement, useId } from 'react'
import { ExtendCurrency } from 'src/enums'
import type { PhysicalCardLifecycleControls } from 'src/feature/PhysicalCardLifecycle/physicalCardLifecycle.types'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'

import CardActionsMenu from '../CardActionsMenu/CardActionsMenu'
import {
  type PhysicalCardActionsMenuOptions,
  physicalCardActionsMenuPropsFor,
} from '../CardActionsMenu/physicalCardActionsMenuPropsFor'
import { initialsFrom, toDisplayAmount } from '../cardDetailHeroFormat'
import type { CardDetailsAvailability } from '../cardDetailHeroMapping'
import { PhysicalCardManagementStatus } from '../cardDetailHeroTypes'
import CardDisplayActions from '../CardDisplayActions/CardDisplayActions'
import CardRevealPanel from '../CardRevealPanel/CardRevealPanel'
import StatusPill from '../StatusPill/StatusPill'
import { useCardDetailHeroData } from '../useCardDetailHeroData'
import {
  useIdentityStateText,
  useLimitsStateText,
} from '../useCardDetailHeroStateText'
import type {
  CardRevealDetails,
  CardRevealState,
} from '../useCardRevealDisclosure'
import * as style from './CardHeroCompact.style'

type CardHeroCompactProps = {
  /** Whether the compact bar is the on-screen variant. Hidden + inert when false. */
  open: boolean
  /** Runtime banner offset; the bar pins at `fixedElementPushdown + globalHeaderHeight`. */
  fixedElementPushdown: number
  /**
   * Whether the sidenav is locked open, so the fixed bar can inset from the left
   * by the correct reserved width and not slide under the sidenav. Read from
   * redux by the page and threaded down (the feature stays redux-free).
   */
  sideNavLockedOpen: boolean
  /** The card whose identity and limits this bar reads; supplied by the page. */
  creditCardId: string | undefined
  /**
   * Shell-owned status, so the compact pill and freeze label agree with the full
   * hero. `undefined` while identity loads or when the backend status has no
   * card-management counterpart.
   */
  status: PhysicalCardManagementStatus | undefined
  lifecycleControls: PhysicalCardLifecycleControls
  /** Shell-owned ••• menu options, so this bar's menu matches the full hero's. */
  actionsMenuOptions: PhysicalCardActionsMenuOptions
  /** Shell-owned reveal disclosure, anchored to this (compact) variant. */
  revealOpen: boolean
  onToggleReveal: () => void
  cardDetailsAvailability: CardDetailsAvailability | undefined
  cardDetailsCapabilityError: boolean
  revealState: CardRevealState
  revealDetails: CardRevealDetails | undefined
  /** Wraps this variant's trigger + panel for the disclosure's outside-click test. */
  revealContainerRef: React.RefObject<HTMLDivElement>
}

/**
 * The pinned condensed hero: a fixed, opaque single-row bar that stands in for
 * the full hero once it scrolls under the global header (plan §4.2). Both heroes
 * stay mounted; this one is `aria-hidden` + `inert` until the swap opens it, so
 * its own (`useId`-generated) reveal-panel id and per-variant test ids keep it
 * from colliding with the full hero, and its reused action controls never enter
 * the a11y tree or tab order while off-screen.
 *
 * Layout, left→right: avatar · name · •• last4 · status pill | divider |
 * Available $X of $Y | Show details / Freeze / •••. No card art (design).
 */
const CardHeroCompact: React.FC<CardHeroCompactProps> = ({
  open,
  fixedElementPushdown,
  sideNavLockedOpen,
  creditCardId,
  status,
  lifecycleControls,
  actionsMenuOptions,
  revealOpen,
  onToggleReveal,
  cardDetailsAvailability,
  cardDetailsCapabilityError,
  revealState,
  revealDetails,
  revealContainerRef,
}): ReactElement => {
  const translate = useTranslate()
  const currencyFormatter = useCurrencyFormatter()
  const identityStateText = useIdentityStateText()
  const limitsStateText = useLimitsStateText()
  const { card, cardState, limits, limitsState } =
    useCardDetailHeroData(creditCardId)

  // This variant's own reveal-panel id (see the shell's note): per-instance so it
  // never collides with the full hero's panel while both are mounted.
  const revealPanelId = useId()

  return (
    <div
      css={style.bar(fixedElementPushdown, open, sideNavLockedOpen)}
      aria-hidden={!open}
      // React 18 has no typed `inert`; apply the boolean attribute by presence
      // (matches src/components/LayoutShell). Removes the off-screen bar from the
      // tab order and a11y tree while it stays mounted for the jank-free swap.
      {...(open ? {} : { inert: '' })}
      data-testid="card-hero-compact"
    >
      <div css={style.identity}>
        {card !== undefined ? (
          <>
            <Avatar
              size={28}
              fallback={
                <span className="fallback-text">
                  {initialsFrom(card.cardholder.displayName)}
                </span>
              }
            />
            <span css={style.cardholderName} data-testid="compact-account-name">
              {card.cardholder.displayName}
            </span>
            <span css={style.last4}>{`•• ${card.last4}`}</span>
            {status !== undefined && <StatusPill status={status} />}
          </>
        ) : (
          <span
            css={style.cardholderName}
            data-testid="compact-identity-state"
            // Only the on-screen variant is a live region — the full hero
            // renders the same message, and two would announce twice.
            role={
              open ? (cardState === 'loading' ? 'status' : 'alert') : undefined
            }
          >
            {identityStateText(cardState)}
          </span>
        )}
      </div>

      <span css={style.divider} aria-hidden="true" />

      <span css={style.available}>
        {limits !== undefined ? (
          <>
            <span css={style.availableLabel}>{translate('Available')}</span>
            <span css={style.availableAmount}>
              {currencyFormatter({
                amount: toDisplayAmount(limits.cardFinancials.availableSpend),
                currency: limits.cardFinancials.availableSpend
                  .currency as ExtendCurrency,
                maximumFractionDigits: 0,
              })}
            </span>
            <span css={style.availableLimit}>
              {translate('of ${limit}', {
                limit: currencyFormatter({
                  amount: toDisplayAmount(
                    limits.permanentCardSpendControl.limit,
                  ),
                  currency: limits.permanentCardSpendControl.limit
                    .currency as ExtendCurrency,
                  maximumFractionDigits: 0,
                }),
              })}
            </span>
            {limits.provenance === 'SURROGATE_PARTNER' && (
              <span css={style.availableLimit} data-testid="compact-provenance">
                {translate('Simulated')}
              </span>
            )}
          </>
        ) : (
          <span
            css={style.availableLimit}
            data-testid="compact-limits-state"
            role={
              open
                ? limitsState === 'loading'
                  ? 'status'
                  : 'alert'
                : undefined
            }
          >
            {limitsStateText(limitsState)}
          </span>
        )}
      </span>

      <div css={style.actions} ref={revealContainerRef}>
        <CardDisplayActions
          freezeAction={lifecycleControls.freezeAction}
          onFreeze={() => void lifecycleControls.toggleFreeze('cardHero')}
          canFreeze={lifecycleControls.canToggleFreeze}
          freezePending={lifecycleControls.isFreezePending}
          revealOpen={revealOpen}
          onToggleReveal={onToggleReveal}
          cardDetailsAvailability={cardDetailsAvailability}
          cardDetailsCapabilityError={cardDetailsCapabilityError}
          revealPanelId={revealPanelId}
        />
        <CardActionsMenu
          {...physicalCardActionsMenuPropsFor(
            lifecycleControls,
            'cardHero',
            actionsMenuOptions,
          )}
        />
        {revealOpen &&
          revealState === 'ready' &&
          revealDetails !== undefined && (
            <CardRevealPanel
              id={revealPanelId}
              state="ready"
              {...revealDetails}
            />
          )}
        {revealOpen && revealState !== 'idle' && revealState !== 'ready' && (
          <CardRevealPanel id={revealPanelId} state={revealState} />
        )}
      </div>
    </div>
  )
}

export default CardHeroCompact
